// SPDX-License-Identifier: AGPL-3.0-or-later

import type {
	CompanionAudioSource,
	CompanionSessionStatus,
	CompanionSessionSummary,
} from '@app/features/companion/commands/CompanionCommands';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

const STARTING_DESCRIPTOR = msg({message: 'Starting…', comment: 'Companion history session status while connecting.'});
const PENDING_DESCRIPTOR = msg({message: 'Pending', comment: 'Companion history session status while pending.'});
const LISTENING_DESCRIPTOR = msg({message: 'Listening', comment: 'Companion history session status while listening.'});
const ENDING_DESCRIPTOR = msg({message: 'Ending…', comment: 'Companion history session status while ending.'});
const ENDED_DESCRIPTOR = msg({message: 'Ended', comment: 'Companion history session status after ending.'});
const FAILED_DESCRIPTOR = msg({message: 'Failed', comment: 'Companion history session status after failure.'});
const INTERRUPTED_DESCRIPTOR = msg({
	message: 'Interrupted',
	comment: 'Companion history session status after interruption.',
});
const UNKNOWN_STATUS_DESCRIPTOR = msg({
	message: 'Unknown',
	comment: 'Companion history session status when unavailable.',
});

const SESSION_STATUS_DESCRIPTORS = {
	pending: PENDING_DESCRIPTOR,
	starting: STARTING_DESCRIPTOR,
	listening: LISTENING_DESCRIPTOR,
	paused: msg({message: 'Paused'}),
	ending: ENDING_DESCRIPTOR,
	ended: ENDED_DESCRIPTOR,
	failed: FAILED_DESCRIPTOR,
	interrupted: INTERRUPTED_DESCRIPTOR,
} satisfies Record<CompanionSessionStatus, MessageDescriptor>;

export function getCompanionSessionStatusDescriptor(status?: CompanionSessionStatus): MessageDescriptor {
	return status ? SESSION_STATUS_DESCRIPTORS[status] : UNKNOWN_STATUS_DESCRIPTOR;
}

export function getCompanionSessionDurationSeconds(session: CompanionSessionSummary, now = Date.now()): number {
	if (!session.started_at) return 0;
	const startedAt = Date.parse(session.started_at);
	if (!Number.isFinite(startedAt)) return 0;
	const endedAt = session.ended_at ? Date.parse(session.ended_at) : now;
	return Math.max(0, ((Number.isFinite(endedAt) ? endedAt : now) - startedAt) / 1000);
}

const MODE_ASSIST_DESCRIPTOR = msg({
	message: 'Assist me',
	comment: 'Companion history session mode where the AI assists the user with suggestions.',
});
const MODE_TALK_DESCRIPTOR = msg({
	message: 'AI chat',
	comment: 'Companion history session mode where the AI chats with the user on its own.',
});
const MICROPHONE_SOURCE_DESCRIPTOR = msg({
	message: 'Personal microphone',
	comment: 'Companion history session audio source using the personal microphone.',
});
const ROOM_SOURCE_DESCRIPTOR = msg({
	message: 'Room conversation',
	comment: 'Companion history session audio source capturing a voice room conversation.',
});

export function getCompanionSessionModeDescriptor(mode: string | undefined): MessageDescriptor | null {
	if (mode === 'assist') return MODE_ASSIST_DESCRIPTOR;
	if (mode === 'talk') return MODE_TALK_DESCRIPTOR;
	return null;
}

export function getCompanionAudioSourceDescriptor(source: CompanionAudioSource | undefined): MessageDescriptor {
	return source?.kind === 'room' ? ROOM_SOURCE_DESCRIPTOR : MICROPHONE_SOURCE_DESCRIPTOR;
}

export interface CompanionSessionDayGroup {
	key: string;
	sessions: Array<CompanionSessionSummary>;
}

export function getCompanionSessionLocalDateKey(date: Date): string {
	const month = `${date.getMonth() + 1}`.padStart(2, '0');
	const day = `${date.getDate()}`.padStart(2, '0');
	return `${date.getFullYear()}-${month}-${day}`;
}

export function getCompanionSessionDayGroups(
	sessions: ReadonlyArray<CompanionSessionSummary>,
): Array<CompanionSessionDayGroup> {
	const groups: Array<CompanionSessionDayGroup> = [];
	const groupsByKey = new Map<string, CompanionSessionDayGroup>();
	for (const session of sessions) {
		const startedAt = session.started_at ? Date.parse(session.started_at) : Number.NaN;
		const key = Number.isFinite(startedAt) ? getCompanionSessionLocalDateKey(new Date(startedAt)) : 'undated';
		let group = groupsByKey.get(key);
		if (!group) {
			group = {key, sessions: []};
			groupsByKey.set(key, group);
			groups.push(group);
		}
		group.sessions.push(session);
	}
	return groups;
}

export function getCompanionSpeakerTone(speakerId: string): number {
	let hash = 0;
	for (let index = 0; index < speakerId.length; index += 1) {
		hash = (hash * 31 + speakerId.charCodeAt(index)) >>> 0;
	}
	return hash % 6;
}
